import { roundTo2Decimals } from '@principle-theorem/accounting';
import { partition, sumBy } from 'lodash';
import { ExactV2TransactionType } from '../../../lib/exact-v2-transaction-type';
import { type IExactV2Transaction } from '../../../source/entities/patient-transactions-v2';
import { type IExactV2TransactionAllocation } from '../../../source/entities/transaction-allocations-v2';
import {
  EXACT_MINIMUM_OWING,
  ExactTransactionLedger,
} from './exact-transaction-ledger';

export interface IExactPaymentSettlement {
  paymentRow: IExactV2Transaction;
  amount: number;
}

export interface IExactChargeAmount {
  charge: IExactV2Transaction;
  amount: number;
}

export interface IExactSettlement {
  payments: IExactPaymentSettlement[];
  unallocated: IExactChargeAmount[];
  claims: IExactChargeAmount[];
}

/**
 * Who paid what against a set of charges. Never synthesises settlement:
 * every figure traces to an allocation leg, a payment row, or `amount_open`.
 */
export class ExactSettlement {
  private _suppliedByPaymentRow = new Map<IExactV2Transaction, number>();

  constructor(private _ledger: ExactTransactionLedger) {}

  /**
   * What the patient still owes on a charge, capped at its gross. Exact's
   * sub-cent residuals and over-settled rows owe nothing.
   */
  static stillOwing(charge: IExactV2Transaction, chargeGross: number): number {
    const isOverSettled =
      Math.sign(charge.amountOpen) === -Math.sign(charge.amount);
    if (isOverSettled) {
      return 0;
    }
    const stillOwing = Math.min(
      chargeGross,
      roundTo2Decimals(Math.abs(charge.amountOpen))
    );
    return stillOwing < EXACT_MINIMUM_OWING ? 0 : stillOwing;
  }

  settle(charges: IExactV2Transaction[]): IExactSettlement {
    return {
      payments: this._allocatedPayments(charges),
      unallocated: this._unallocatedResiduals(charges),
      claims: this._pendingClaims(charges),
    };
  }

  private _allocatedPayments(
    charges: IExactV2Transaction[]
  ): IExactPaymentSettlement[] {
    const appliedByPaymentRow = new Map<IExactV2Transaction, number>();

    for (const charge of charges) {
      const chargeGross = roundTo2Decimals(Math.abs(charge.amount));
      const [reversals, receipts] = partition(
        this._ledger.allocationsFor(charge),
        (allocation) => this._isReversingAdjustment(allocation)
      );

      // Exact over-allocates. Receipts claim capacity before reversals.
      let chargeConsumed = 0;
      for (const allocation of [...receipts, ...reversals]) {
        const remainingCapacity = roundTo2Decimals(
          chargeGross - chargeConsumed
        );
        if (remainingCapacity <= 0) {
          break;
        }
        chargeConsumed = roundTo2Decimals(
          chargeConsumed +
            this._applyAllocation(
              appliedByPaymentRow,
              allocation,
              remainingCapacity
            )
        );
      }
    }

    return Array.from(appliedByPaymentRow.entries()).flatMap(
      ([paymentRow, amount]) =>
        amount > 0 ? [{ paymentRow, amount: roundTo2Decimals(amount) }] : []
    );
  }

  private _applyAllocation(
    appliedByPaymentRow: Map<IExactV2Transaction, number>,
    allocation: IExactV2TransactionAllocation,
    remainingCapacity: number
  ): number {
    const paymentRow = this._ledger.paymentRow(allocation);
    if (!paymentRow) {
      return 0;
    }
    const applied = roundTo2Decimals(
      Math.min(Math.abs(allocation.amount), remainingCapacity)
    );
    if (applied <= 0) {
      return 0;
    }
    return this._draw(appliedByPaymentRow, paymentRow, applied, new Set());
  }

  /**
   * A payment never emits more than its face value across the whole account.
   * Exact allocates a payment beyond its face value when other rows (e.g. a
   * prompt-payment discount) were allocated against that payment to top it
   * up, so the shortfall is drawn from those rows instead.
   */
  private _draw(
    appliedByPaymentRow: Map<IExactV2Transaction, number>,
    paymentRow: IExactV2Transaction,
    requested: number,
    visitedPaymentRows: Set<IExactV2Transaction>
  ): number {
    visitedPaymentRows.add(paymentRow);
    const supplied = this._suppliedByPaymentRow.get(paymentRow) ?? 0;
    const drawn = roundTo2Decimals(
      Math.max(0, Math.min(requested, Math.abs(paymentRow.amount) - supplied))
    );
    if (drawn > 0) {
      this._suppliedByPaymentRow.set(
        paymentRow,
        roundTo2Decimals(supplied + drawn)
      );
      appliedByPaymentRow.set(
        paymentRow,
        roundTo2Decimals((appliedByPaymentRow.get(paymentRow) ?? 0) + drawn)
      );
    }

    let total = drawn;
    for (const funding of this._ledger.allocationsFor(paymentRow)) {
      const shortfall = roundTo2Decimals(requested - total);
      if (shortfall <= 0) {
        break;
      }
      const funder = this._ledger.paymentRow(funding);
      if (!funder || visitedPaymentRows.has(funder)) {
        continue;
      }
      total += this._draw(
        appliedByPaymentRow,
        funder,
        Math.min(shortfall, Math.abs(funding.amount)),
        visitedPaymentRows
      );
    }
    visitedPaymentRows.delete(paymentRow);
    return roundTo2Decimals(total);
  }

  private _isReversingAdjustment(
    allocation: IExactV2TransactionAllocation
  ): boolean {
    return (
      this._ledger.paymentRow(allocation)?.transactionType ===
      ExactV2TransactionType.Adjustment
    );
  }

  /**
   * Consumed by Exact but shipped with no allocation. Absent payment rows
   * still count as coverage, so our own extraction defects stay owing. The
   * exception is a holder offsetting one claim against another row from a
   * ledger this patient never carries.
   */
  private _unallocatedResiduals(
    charges: IExactV2Transaction[]
  ): IExactChargeAmount[] {
    return charges.flatMap((charge) => {
      const chargeGross = roundTo2Decimals(Math.abs(charge.amount));
      const allocated = roundTo2Decimals(
        sumBy(
          this._ledger
            .allocationsFor(charge)
            .filter(
              (allocation) =>
                !this._isOffsetFromAnotherLedger(charge, allocation)
            ),
          (allocation) => Math.abs(allocation.amount)
        )
      );
      const settled = roundTo2Decimals(
        chargeGross - ExactSettlement.stillOwing(charge, chargeGross)
      );
      const amount = roundTo2Decimals(
        settled - Math.min(allocated, chargeGross)
      );
      return amount > 0 ? [{ charge, amount }] : [];
    });
  }

  private _isOffsetFromAnotherLedger(
    charge: IExactV2Transaction,
    allocation: IExactV2TransactionAllocation
  ): boolean {
    return (
      (charge.isAgreementFunded === true ||
        allocation.paymentTransactionType === ExactV2TransactionType.Charge) &&
      !this._ledger.paymentRow(allocation)
    );
  }

  private _pendingClaims(charges: IExactV2Transaction[]): IExactChargeAmount[] {
    return charges.flatMap((charge) => {
      if (charge.amount <= 0 || !charge.isAgreementFunded) {
        return [];
      }
      const amount = ExactSettlement.stillOwing(
        charge,
        roundTo2Decimals(Math.abs(charge.amount))
      );
      return amount > 0 ? [{ charge, amount }] : [];
    });
  }
}
